import { ReactComponent as CheckBoxDefault } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckBoxFilled } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";

import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import zIndex from "@mui/material/styles/zIndex";
import { cloneDeep } from "lodash";

import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton } from "components/buttons/SolidButton";
import ImportView from "./ImportView";
import LoadingView from "./LoadingView";

const ipAddressCSVExample = [
  ["IP Address", "Description"],
  ["192.168.20.0/24", "Office"],
  ["192.168.21.0/24", "Guest Rooms"],
  ["192.168.22.5/32", ""],
  ["192.168.23.0/24", "Conference Rooms"],
  ["192.168.24.0/24", "Security Cameras"],
];

const UploadView = ({
  form,
  onClose,
  onImport,
}) => {
  const theme = useTheme();
  const viewRef = useRef(null);
  const [openImportView, setOpenImportView] = useState(false);
  const [ipAddressItemList, setIpAddressItemList] = useState([]);
  const [replaceFlag, setReplaceFlag] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const downloadCSVExample = () => {
    const csvContent = "data:text/csv;charset=utf-8,"
      + cloneDeep(ipAddressCSVExample).map(row => row.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "ip_address_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = () => {
    if (ipAddressItemList.length === 0 || isImporting) return;

    setIsImporting(true);
    // Yield once so the loading overlay paints before the parent table updates.
    setTimeout(() => {
      onImport(replaceFlag, ipAddressItemList);
      onClose();
    }, 0);
  };

  let viewHeight = 0;
  let marginTop = 0;
  if (viewRef && viewRef.current) {
    viewHeight = viewRef.current.offsetHeight;
    marginTop = viewHeight / 6;
  }
  const parentViewWidth = viewRef && viewRef.current ? viewRef.current.offsetWidth : 0;
  const parentViewHeight = viewRef && viewRef.current ? viewRef.current.offsetHeight : 0;

  if (isImporting) {
    return <LoadingView />;
  }

  return (
    <Box ref={viewRef} style={{ position: "relative", padding: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          Add IP Address by Upload
        </Typography>
        <Button
          onClick={onClose}
          disabled={isImporting}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": { backgroundColor: theme.palette.primary.transparent },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", margin: "20px 10px 10px" }}>
        <OutlineRectangleButton startIcon={<AddIcon />} height="32px" onClick={downloadCSVExample} disabled={isImporting}>
          Download CVS Sample
        </OutlineRectangleButton>
      </Box>

      <Box
        sx={{
          border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
          borderRadius: "4px",
          margin: "0 10px 0 20px",
        }}
      >
        <Box sx={{ ...form.getTableStyle(), height: "300px", padding: "0 10px" }}>
          <TableContainer
            component={Paper}
            sx={{ "&.MuiPaper-root": { boxShadow: "none", border: "none" } }}
            style={{ overflowY: "auto" }}
          >
            <Table sx={{ borderCollapse: "collapse", "& td, & th": { border: "none" } }}>
              <TableHead>
                <TableRow style={{ borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}` }}>
                  <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ipAddressItemList.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{`${item.ipAddress}/${item.subnet}`}</TableCell>
                    <TableCell>{item.description || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "10px 10px 20px 20px" }}>
        <Box sx={form.getLabelStyle()}>IP Address: {ipAddressItemList.length}</Box>
        <OutlineRectangleButton startIcon={<AddIcon />} height="32px" onClick={() => setOpenImportView(true)} disabled={isImporting}>
          Upload
        </OutlineRectangleButton>
      </Box>

      <Box sx={{ marginLeft: "10px", marginBottom: "10px" }}>
        <Checkbox
          icon={<CheckBoxDefault style={{ width: "24px", height: "24px" }} />}
          checkedIcon={<CheckBoxFilled style={{ width: "24px", height: "24px" }} />}
          checked={replaceFlag}
          disabled={isImporting}
          onChange={(e) => setReplaceFlag(e.target.checked)}
        />
        <Typography component="span" sx={{ fontSize: theme.typography.customFontSize[2] }}>
          Replace the existing IP address in the list.
        </Typography>
      </Box>

      <Box sx={{ marginLeft: "20px", marginBottom: "30px" }}>
        <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>Note:</Typography>
        <Typography sx={{ fontSize: theme.typography.customFontSize[2], marginLeft: "10px" }}>
          1. Each IP address and its description on a separate line, with a space between them.
        </Typography>
      </Box>

      <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
        <OutlineButton onClick={onClose} sx={{ marginRight: "20px" }} width="100px" height="32px" disabled={isImporting}>
          Cancel
        </OutlineButton>
        <SolidButton onClick={handleImport} width="100px" height="32px" disabled={ipAddressItemList.length === 0 || isImporting}>
          Import
        </SolidButton>
      </Box>

      {openImportView && (
        <Box
          style={{
            ...form.getMaskStyle(),
            width: parentViewWidth + 40 + "px",
            height: parentViewHeight + 60 + "px",
            transform: "translateY(-20px) translateX(-20px)",
          }}
        />
      )}

      {openImportView && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: parentViewWidth + 40 + "px",
            transform: `translate(-20px, ${marginTop - 40}px)`,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <ImportView
              form={form}
              onClose={() => setOpenImportView(false)}
              onUpload={setIpAddressItemList}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(UploadView);
